import clsx from 'clsx';

export interface SparklineProps {
  /** Ordered series — earliest sample first. Captured by `recordEdit()`'s
   *  metricHistory writeback (Phase 1) on every state-mutating turn. */
  values: number[];
  /** SVG width in px. Default 80. */
  width?: number;
  /** SVG height in px. Default 12. Spec target range: 8–12px. */
  height?: number;
  /**
   * Direction-of-improvement for this metric. When the actual trend
   * (last vs first) opposes `positiveTrend`, the line color shifts to
   * `var(--color-warn)`. Without this prop, the stroke is always
   * `var(--color-accent)`.
   */
  positiveTrend?: 'up' | 'down';
  /** Optional className for the wrapper element. */
  className?: string;
}

const DEFAULT_WIDTH = 80;
const DEFAULT_HEIGHT = 12;
const Y_PADDING_RATIO = 0.05;
const STROKE_WIDTH = 1;
const SINGLE_DOT_RADIUS = 1.5;

type StrokeColor = 'accent' | 'warn';

function classifyTrend(
  values: number[],
  positiveTrend?: 'up' | 'down',
): StrokeColor {
  if (!positiveTrend || values.length < 2) return 'accent';
  const first = values[0];
  const last = values[values.length - 1];
  if (first === last) return 'accent';
  const direction: 'up' | 'down' = last > first ? 'up' : 'down';
  return direction === positiveTrend ? 'accent' : 'warn';
}

const STROKE_TOKEN: Record<StrokeColor, string> = {
  accent: 'var(--color-accent)',
  warn: 'var(--color-warn)',
};

/**
 * Pure-SVG sparkline primitive. Auto-scales the y-axis to series min/max
 * with 5% padding so a near-flat series still shows a visible line.
 *
 * Render branches:
 * - `values.length === 0`: "No history yet" text placeholder (per I7
 *   lazy-populate — pre-Phase-1 conversations have empty `metricHistory`)
 * - `values.length === 1`: single dot at horizontal center
 * - `values.length >= 2`: polyline traversing every sample
 *
 * Reused in Phase 6 Hero Metrics row — keep the shape additive.
 */
export function Sparkline({
  values,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  positiveTrend,
  className,
}: SparklineProps) {
  if (values.length === 0) {
    return (
      <span
        data-testid="sparkline"
        data-state="empty"
        className={clsx(
          'inline-flex items-center text-micro font-mono text-text-dim',
          className,
        )}
        style={{ width, height }}
      >
        No history yet
      </span>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  // Pad y range so a constant series still draws a centered line
  // (otherwise ySpan === 0 → divide-by-zero → NaN coords).
  const padding =
    range === 0 ? Math.max(Math.abs(min) * Y_PADDING_RATIO, 1) : range * Y_PADDING_RATIO;
  const yMin = min - padding;
  const yMax = max + padding;
  const ySpan = yMax - yMin || 1;

  const xCoord = (i: number): number => {
    if (values.length === 1) return width / 2;
    return (i / (values.length - 1)) * width;
  };
  const yCoord = (v: number): number => {
    // SVG y-axis is inverted — smaller `y` is higher on screen.
    return height - ((v - yMin) / ySpan) * height;
  };

  const strokeColor = classifyTrend(values, positiveTrend);
  const stroke = STROKE_TOKEN[strokeColor];
  const trendMatchAttr =
    positiveTrend && values.length >= 2
      ? strokeColor === 'accent'
        ? 'true'
        : 'false'
      : undefined;

  if (values.length === 1) {
    return (
      <svg
        data-testid="sparkline"
        data-state="single"
        width={width}
        height={height}
        className={className}
        role="img"
        aria-label="Metric history: 1 sample"
      >
        <circle
          cx={xCoord(0)}
          cy={yCoord(values[0])}
          r={SINGLE_DOT_RADIUS}
          fill={stroke}
        />
      </svg>
    );
  }

  const points = values
    .map((v, i) => `${xCoord(i).toFixed(2)},${yCoord(v).toFixed(2)}`)
    .join(' ');

  return (
    <svg
      data-testid="sparkline"
      data-state="line"
      data-trend-match={trendMatchAttr}
      width={width}
      height={height}
      className={className}
      role="img"
      aria-label={`Metric history: ${values.length} samples`}
    >
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
